import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";

const COLUMNS = [
  { key: "title", label: "Title", className: "w-[48%]" },
  { key: "authors", label: "Authors", className: "w-[32%]" },
  { key: "year", label: "Year", className: "w-[10%]" },
];

const sortValue = (c, key) =>
  key === "authors" ? (c.authors || []).join("; ").toLowerCase() : key === "year" ? parseInt(c.year, 10) || 0 : (c.title || "").toLowerCase();

function ReadDot({ citation, onToggle }) {
  const read = !!citation.read;
  return (
    <button
      data-testid={`read-toggle-${citation.id}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(citation);
      }}
      title={read ? "Mark as unread" : "Mark as read"}
      aria-label={read ? "Mark as unread" : "Mark as read"}
      className="w-6 h-6 grid place-items-center rounded-full hover:bg-[#FCE7F3] transition-colors"
    >
      <span
        data-testid={`read-state-${citation.id}`}
        data-read={read}
        className={`w-2.5 h-2.5 rounded-full border-2 transition-colors ${read ? "border-[#BBA9CF] bg-transparent" : "border-[#C9286F] bg-[#C9286F]"}`}
      />
    </button>
  );
}

export default function CitationTable({ citations, selectedId, onSelect, onToggleRead, onDelete }) {
  const [sort, setSort] = useState({ key: "title", dir: 1 });
  const rows = useMemo(
    () =>
      [...citations].sort((a, b) => {
        const va = sortValue(a, sort.key);
        const vb = sortValue(b, sort.key);
        return (va > vb ? 1 : va < vb ? -1 : 0) * sort.dir;
      }),
    [citations, sort]
  );
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));

  return (
    <div className="rounded-2xl border-2 border-[#F5D0E3] bg-white overflow-hidden" data-testid="citation-list-container">
      <table className="w-full table-fixed text-sm">
        <thead className="bg-[#FDF2F8] text-[#5E4B70]">
          <tr>
            <th className="w-10" aria-label="Read status" />
            {COLUMNS.map((col) => (
              <th key={col.key} className={`${col.className} text-left font-semibold px-3 py-2.5`}>
                <button
                  data-testid={`sort-${col.key}`}
                  onClick={() => toggleSort(col.key)}
                  className="inline-flex items-center gap-1 hover:text-[#C9286F] transition-colors"
                >
                  {col.label}
                  {sort.key === col.key && (sort.dir === 1 ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
                </button>
              </th>
            ))}
            <th className="w-12" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const active = c.id === selectedId;
            return (
              <tr
                key={c.id}
                data-testid={`citation-row-${c.id}`}
                onClick={() => onSelect(c.id)}
                className={`group cursor-pointer border-t border-[#FCE7F3] transition-colors ${
                  active ? "bg-[#FCE4F0]" : "hover:bg-[#FFF5FA]"
                }`}
              >
                <td className="pl-2">
                  <ReadDot citation={c} onToggle={onToggleRead} />
                </td>
                <td className={`px-3 py-3 text-[#3B2350] truncate ${c.read ? "font-normal" : "font-bold"}`} title={c.title}>
                  {c.title}
                </td>
                <td className="px-3 py-3 text-[#5E4B70] truncate" title={(c.authors || []).join("; ")}>
                  {(c.authors || []).join("; ") || "Unknown author"}
                </td>
                <td className="px-3 py-3 text-[#5E4B70] tabular-nums">{c.year || "n.d."}</td>
                <td className="pr-2 text-right">
                  <button
                    data-testid={`delete-citation-${c.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Delete "${c.title}"?`)) onDelete(c.id);
                    }}
                    aria-label="Delete citation"
                    className="w-8 h-8 inline-grid place-items-center rounded-full text-[#7A6A8C] opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-[#C9286F] hover:bg-[#FCE7F3] transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
